import type { ReactNode } from "react";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import { CloseIcon } from "./Icons.tsx";
import { NoticeCoin } from "./Section.tsx";

/**
 * What happened, said once, where the owner is already looking.
 *
 * The mobile app answers a move or a cancel with `Alert.alert`. A web alert is a browser chrome box
 * in the browser's own typeface, and it is gone before the owner can re-read the number in it. So
 * the answer lands as a banner at the top of the screen content instead, and stays until dismissed:
 * "3 bookings moved and 3 members notified" is a fact worth checking twice.
 *
 * `role="status"` rather than `alert`, because a screen reader should finish the sentence it is on
 * before announcing a success. The failure banner below uses `role="alert"`, which is the case that
 * does justify interrupting.
 */
export function SuccessNotice({
  title,
  message,
  onDismiss,
}: {
  title: string;
  message: string;
  onDismiss: () => void;
}) {
  return (
    <output className="notice notice-success mb-4 flex items-start gap-3 rounded-card px-4 py-3.5">
      <span className="shrink-0">
        <NoticeCoin tone="success" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold">{title}</p>
        <p className="mt-0.5 text-[13px] opacity-90">{message}</p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-mt-1 -mr-1 flex size-8 shrink-0 items-center justify-center rounded-full opacity-75 transition duration-150 hover:opacity-100 active:bg-confirmed/10"
      >
        <CloseIcon size={15} />
      </button>
    </output>
  );
}

/**
 * A failed action, in the copy the error registry provides.
 *
 * Used inside a sheet, where the whole screen must not be replaced by an `ErrorState`: the owner is
 * halfway through a decision and needs the form they were filling in to still be there.
 */
export function InlineError({ error }: { error: unknown }) {
  const copy = copyForError(error);
  return (
    <div role="alert" className="notice notice-danger mb-4 flex gap-2.5 rounded-card px-4 py-3.5">
      <NoticeCoin tone="danger" />
      <div className="min-w-0">
        <p className="text-[14px] font-semibold">{copy.title}</p>
        <p className="mt-0.5 text-[13px] opacity-90">{copy.message}</p>
      </div>
    </div>
  );
}

/**
 * A block of context inside a sheet: what this action is about to do, or already did. Raised like
 * every message box; `quiet` is grey with an "i", `late` is the gold warning box.
 */
export function Callout({
  tone = "quiet",
  title,
  children,
}: {
  tone?: "quiet" | "late";
  title: string;
  children: ReactNode;
}) {
  const coin = tone === "late" ? "warning" : "quiet";

  return (
    <div className={`notice notice-${coin} mb-4 flex gap-2.5 rounded-card px-4 py-3.5`}>
      <NoticeCoin tone={coin} />
      <div className="min-w-0">
        <p className="text-[14px] font-semibold">{title}</p>
        <div className="mt-0.5 text-[13px] opacity-90">{children}</div>
      </div>
    </div>
  );
}
